import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'components';
import cx from 'clsx';
import QRCode from '@/components/QRCode';
import { IconRcArrowRight, IconRcDebankAppIcon, IconRcQRCodeRefreshIcon } from '@/assets';
import style from './index.module.css';
import { useCountdown } from '@/hooks';
import { GenerateQRCode, useGenerateQRCode, useGetQRCodeStatus } from '../../hooks';
import { IconBtnLoading } from 'assets';

const { CenterModal } = Modal;

function RealQRCode({ switchLogin }: { switchLogin: ScanQRCodeModalProps['switchLogin'] }) {
  const { t } = useTranslation();

  const [isActive, setIsActive] = useState(false);

  const { seconds, startCountdown } = useCountdown(90);

  const [startCount, setStartCount] = useState(false);

  const { data, refetch, isFetching } = useGenerateQRCode();
  const [qrcodeStatus, setQRCodeStatus] = useState<'generated' | 'scanned' | 'confirmed'>(
    'generated',
  );

  const { data: codeStatus } = useGetQRCodeStatus(data?.id || '', qrcodeStatus);
  const requestQRCode = useCallback(() => {
    refetch();
    startCountdown();
  }, [refetch, startCountdown]);

  const refreshQRCode = useCallback(() => {
    if (!isActive) {
      setIsActive(true);
      requestQRCode();
      const timer = setTimeout(() => {
        setIsActive(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isActive, requestQRCode]);

  useEffect(() => {
    if (seconds === 0) {
      requestQRCode();
    }
  }, [seconds, requestQRCode]);

  useEffect(() => {
    if (codeStatus?.state === 'scanned') {
      setQRCodeStatus(codeStatus?.state);
      codeStatus && switchLogin(codeStatus);
    }
  }, [codeStatus, codeStatus?.state, qrcodeStatus, requestQRCode, switchLogin]);
  useEffect(() => {
    if (!startCount && data?.id) {
      setStartCount(true);
      startCountdown();
    }
  }, [startCount, startCountdown, data?.id]);

  return (
    <>
      <div className={style.qrcodeContainer}>
        {isFetching ? (
          <IconBtnLoading />
        ) : (
          data?.id && <QRCode url={data?.id} size={180} level={'H'} />
        )}
      </div>

      {data?.id ? (
        <div className={style.refresh}>
          <IconRcQRCodeRefreshIcon
            onClick={refreshQRCode}
            className={cx(style.refreshIcon, isActive && style.refreshActive)}
          />
          <div>
            {t('APPScanQRCodeModal.rn7iju')}
            <span className={style.seconds}> {seconds} </span>
            {t('APPScanQRCodeModal.wky0e7')}
          </div>
        </div>
      ) : (
        <div className={style.refresh} />
      )}
    </>
  );
}

type ScanQRCodeModalProps = {
  turnOff(): void;
  switchLogin(data: GenerateQRCode): void;
};
export const APPScanQRCodeModal = ({ turnOff, switchLogin }: ScanQRCodeModalProps) => {
  const { t } = useTranslation();

  const contentRef = useRef<Element>();
  const overlayRef = useRef<Element>();

  return (
    <CenterModal
      className="scan-qrcode-modal"
      style={{ content: { overflow: 'visible', outline: 'none' } }}
      onRequestClose={turnOff}
      contentRef={n => (contentRef.current = n)}
      overlayRef={n => (overlayRef.current = n)}>
      <div className={style.qrcodeModal}>
        <IconRcDebankAppIcon className={style.qrcodeLogo} />
        <div className={style.qrcodeTitle}>{t('APPScanQRCodeModal.152bou3')}</div>
        <RealQRCode switchLogin={switchLogin} />
        <div className={style.bottom}>
          <span className={style.bottomTitle1}>{t('APPScanQRCodeModal.18uyk42')}</span>
          <span className={style.bottomTitle2}>
            {t('APPScanQRCodeModal.plbf23')}{' '}
            <a href="/download" target="_blank" className={style.downloadLink}>
              {t('APPScanQRCodeModal.11g7pxk')}
              <IconRcArrowRight className={style.arrow} />
            </a>
          </span>
        </div>
      </div>
    </CenterModal>
  );
};
export default React.memo(APPScanQRCodeModal);
